/**
 * Messages that can't be answered as they stand, said with nothing open to refer to: "Find it",
 * "Delete that", "The other Alex", "Build our school website". Each gets one plain reply that says
 * what's missing and what to say instead, rather than a guess at unrelated questions.
 */

export type VagueReply =
  | { kind: "didYouMean"; text: string; questions: string[] }
  | { kind: "offTopic"; text: string }
  | { kind: "cancelled"; text: string; choices: string[] };

/** "Actually leave it", "forget it": nothing was started, so nothing to stop. */
const LEAVE_IT = /^(?:(?:actually|ok(?:ay)?|no|oh)[,\s]+)?(?:leave it|forget it|never ?mind|don'?t bother|skip it|scrap that)(?:\s+(?:then|for now))?[.!]*$/i;

/** "the other Alex", "Alex, not the first Alex": one of two people with the same name. */
const THE_OTHER_PERSON = /^(?:[Nn]o[,\s]+)?(?:[Tt]he\s+other|[Aa]nother)\s+([A-Z][a-z]+)\b|^([A-Z][a-z]+)[,\s]+not\s+the\s+(?:first|other|last)\b/;

/** A big piece of work that isn't Noticeboard's: a website, a curriculum, a whole project. */
const BIG_OUTSIDE_JOB =
  /\b(?:build|make|create|design|write|plan|run|do)\b[^.?!]*\b(?:websites?|web\s*sites?|apps?|curriculum|curricula|(?:year|term)\s+of\s+lessons|annual\s+\w+|marketing|launch|business\s+plan|budget|timetable|project)\b/i;
/** Words that keep it Noticeboard's: "plan an event", "make a notice about the project". */
const NOTICEBOARD_THING = /\b(?:notices?|polls?|events?|calendar|circles?|drive|library|certificates?|awards?|claim\s+lists?|templates?)\b/i;

/** "Find it", "Delete that", "Send the same one again", "Is that the right one?": which one isn't said. */
const POINTS_AT_NOTHING =
  /^(?:please\s+)?(?:(find|look\s+up|open|show)|(delete|remove|take\s+down)|(send|resend|repeat)|(use|change)|is)\b[^.?!]{0,40}?\b(?:it|that|this|that\s+one|this\s+one|the\s+(?:same|previous|old|last|other|right)\s+(?:one|version|class|group)|my\s+usual\s+(?:group|class|circle))\b(?:\s+(?:again|please|for\s+me|one))*[?.!]*$/i;

const OUTSIDE_REPLY =
  "That's bigger than what I do here. I help with Noticeboard: finding things, explaining how it works, and preparing Notices, Polls and Events for you to check before they're sent.";

/** What to suggest for each kind of request, as catalogue examples; only the ones this person has are kept. */
const SUGGEST = {
  find: ["Where can I see the notices we've sent?", "Find a file", "Find a user"],
  delete: ["How do I delete a notice so parents never saw it?", "Where can I see the notices we've sent?"],
  send: ["Can you send a notice for me?", "How do I send a reminder about a notice?", "How do I edit a sent notice?"],
  audience: ["How do I choose who receives a notice?", "Can you send a notice for me?"],
  check: ["Where can I see the notices we've sent?", "Do I have any drafts?"],
  person: ["Find a user"],
  outside: [] as string[],
};

/**
 * A reply for a message that names nothing to act on, or null when the message can be answered as usual.
 * `hasExample` says whether a suggestion is one this person's catalogue can answer.
 */
export function vagueRequestReply(text: string, hasExample: (question: string) => boolean): VagueReply | null {
  const said = text.trim();
  const keep = (questions: string[]) => questions.filter(hasExample);

  if (LEAVE_IT.test(said)) return { kind: "cancelled", text: "No problem, nothing was started or changed.", choices: [] };

  const person = said.match(THE_OTHER_PERSON);
  const name = person?.[1] ?? person?.[2];
  if (name) {
    return {
      kind: "didYouMean",
      text: `Which ${name} do you mean? Tell me their full name, like “Find a user named ${name} Smith”.`,
      questions: keep(SUGGEST.person),
    };
  }

  if (BIG_OUTSIDE_JOB.test(said) && !NOTICEBOARD_THING.test(said)) return { kind: "offTopic", text: OUTSIDE_REPLY };

  const pointed = said.match(POINTS_AT_NOTHING);
  if (pointed) {
    const [, find, remove, send, use] = pointed;
    const questions = find ? SUGGEST.find : remove ? SUGGEST.delete : send ? SUGGEST.send : use ? SUGGEST.audience : SUGGEST.check;
    const what = find ? "find" : remove ? "remove" : send ? "send" : use ? "change" : "check";
    return {
      kind: "didYouMean",
      text: `Which one should I ${what}? Nothing is open in this chat yet, so tell me its name, like “the Sports Day notice”.`,
      questions: keep(questions),
    };
  }
  return null;
}
